import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Lock, Loader2, Clock, Check, Plus } from "lucide-react";
import { isDoublesLine, categoryLabel } from "@/lib/clubwars";
import api, { formatApiError } from "@/lib/api";

export default function CaptainPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/cw/captain/${token}`).then(({ data }) => { setData(data); setErr(false); })
      .catch((e) => { if (e.response?.status === 404) setErr(true); });
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  if (err) return <Center>Invalid or expired captain link.</Center>;
  if (!data) return <Center><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></Center>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
        <div className="flex items-center gap-3 mb-2 min-w-0"><Swords className="w-7 h-7 text-primary-c shrink-0" /><h1 className="font-display font-bold text-3xl break-words min-w-0">{data.name}</h1></div>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Captain portal · <span className="font-bold text-primary-c">{data.team_name}</span>{data.venue ? ` · ${data.venue}` : ""}</p>
        {data.lineup_deadline && (
          <div className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${data.deadline_passed ? "text-red-400" : "text-primary-c"}`} style={{ background: "var(--surface)" }} data-testid="captain-deadline">
            <Clock className="w-4 h-4" /> {data.deadline_passed ? "Line-up deadline has passed" : `Submit by ${new Date(data.lineup_deadline).toLocaleString()}`}
          </div>
        )}

        <RosterManager token={token} roster={data.roster} rosterLocked={data.roster_locked} onSaved={setData} />

        <div className="mt-6 space-y-5">
          {data.fixtures.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No fixtures scheduled for your team yet.</p>}
          {data.fixtures.map((fx) => (
            <FixtureLineup key={fx.fixture_id} token={token} fx={fx} roster={data.roster} deadlinePassed={data.deadline_passed} onSaved={setData} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RosterManager({ token, roster, rosterLocked, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/public/cw/captain/${token}/add-player`, { name: name.trim() }); setName(""); onSaved(data); toast.success("Player added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add player"); }
    finally { setBusy(false); }
  };
  const toggleLock = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/public/cw/captain/${token}/roster-lock`, { locked: !rosterLocked }); onSaved(data); toast.success(rosterLocked ? "Roster unlocked" : "Final roster submitted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update roster"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="captain-roster">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold">Your roster</h3>
        {rosterLocked && <span data-testid="captain-roster-locked" className="inline-flex items-center gap-1 text-[10px] mono uppercase rounded-full px-2 py-0.5" style={{ background: "rgba(34,197,94,0.14)", color: "#22c55e" }}><Check className="w-3 h-3" /> Submitted</span>}
      </div>
      <p className="text-xs mt-1 mb-3" style={{ color: "var(--text-2)" }}>Add players in <span className="font-bold">Last Name, First Name</span> format, then submit your final roster.</p>
      {roster.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {roster.map((p) => <span key={p.id} className="glass rounded-full px-2.5 py-0.5 text-xs">{p.name}</span>)}
        </div>
      )}
      {!rosterLocked && (
        <div className="flex items-center gap-2">
          <input data-testid="captain-add-player-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Waters, Anna Leigh" className="flex-1 rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none" style={{ color: "var(--text)" }} />
          <button data-testid="captain-add-player" onClick={add} disabled={busy} className="btn-glow rounded-full px-4 py-2 text-sm font-bold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</button>
        </div>
      )}
      <button data-testid="captain-roster-lock" onClick={toggleLock} disabled={busy} className={`mt-3 rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50 ${rosterLocked ? "glass" : "btn-glow"}`} style={rosterLocked ? { color: "var(--text)" } : {}}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : rosterLocked ? <Lock className="w-4 h-4" /> : <Check className="w-4 h-4" />} {rosterLocked ? "Unlock roster" : "Submit final roster"}
      </button>
    </div>
  );
}

function FixtureLineup({ token, fx, roster, deadlinePassed, onSaved }) {
  const slots = fx.slots || [];
  const [draft, setDraft] = useState(() => {
    const d = {}; slots.forEach((s) => { d[s.slot_key] = fx.lineup?.[s.slot_key] || []; }); return d;
  });
  const [busy, setBusy] = useState(false);
  const locked = fx.locked;
  const setSlot = (lid, s, pid) => setDraft((d) => { const a = [...(d[lid] || [])]; a[s] = pid; return { ...d, [lid]: a }; });

  const submit = async (lock) => {
    setBusy(true);
    const assignments = {};
    Object.entries(draft).forEach(([lid, arr]) => { assignments[lid] = (arr || []).filter(Boolean); });
    try {
      let res = await api.post(`/public/cw/captain/${token}/lineup`, { fixture_id: fx.fixture_id, assignments });
      if (lock) res = await api.post(`/public/cw/captain/${token}/lock`, { fixture_id: fx.fixture_id });
      toast.success(lock ? "Line-up locked" : "Line-up saved");
      onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  const addEntry = async (lid) => {
    try { const { data } = await api.post(`/public/cw/captain/${token}/add-entry`, { line_id: lid }); onSaved(data); toast.success("Extra entry added — assign the new slot"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add entry"); }
  };

  const lineGroups = [];
  slots.forEach((sl) => {
    const lid = (sl.slot_key || "").split(":")[0];
    if (!lineGroups.find((g) => g.lid === lid)) lineGroups.push({ lid, label: categoryLabel({ type: sl.type, skill: sl.skill }) });
  });

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid={`captain-fixture-${fx.fixture_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div><div className="font-display font-bold">vs {fx.opponent_name}</div><div className="text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>{fx.round_label}</div></div>
        {locked && <span className="text-[10px] mono uppercase text-primary-c flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
      </div>
      <div className="space-y-2">
        {slots.map((sl) => {
          const n = sl.need || (isDoublesLine(sl.type) ? 2 : 1);
          return (
            <div key={sl.slot_key} className="flex flex-wrap items-center gap-2">
              <span className="text-xs w-44 shrink-0" style={{ color: "var(--text-2)" }}>{categoryLabel(sl)}</span>
              {Array.from({ length: n }).map((_, s) => (
                <select key={s} data-testid={`captain-slot-${fx.fixture_id}-${sl.slot_key}-${s}`} disabled={locked || deadlinePassed} value={draft[sl.slot_key]?.[s] || ""}
                  onChange={(e) => setSlot(sl.slot_key, s, e.target.value)}
                  className="rounded-lg glow-border bg-surface px-2 py-1.5 text-sm outline-none disabled:opacity-60" style={{ color: "var(--text)" }}>
                  <option value="">— player —</option>
                  {roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              ))}
            </div>
          );
        })}
      </div>
      {!locked && !deadlinePassed && (
        <>
          {lineGroups.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`captain-add-entry-row-${fx.fixture_id}`}>
              {lineGroups.map((g) => (
                <button key={g.lid} data-testid={`captain-add-entry-${fx.fixture_id}-${g.lid}`} onClick={() => addEntry(g.lid)}
                  className="glass rounded-full px-2.5 py-1 text-[11px] font-bold flex items-center gap-1" style={{ color: "var(--text)" }}>
                  <Plus className="w-3 h-3 text-primary-c" /> {g.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <button data-testid={`captain-save-${fx.fixture_id}`} onClick={() => submit(false)} disabled={busy} className="glass rounded-full px-4 py-2 text-sm font-bold disabled:opacity-50" style={{ color: "var(--text)" }}>Save draft</button>
            <button data-testid={`captain-lock-${fx.fixture_id}`} onClick={() => submit(true)} disabled={busy} className="btn-glow rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Submit & lock</button>
          </div>
        </>
      )}
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{children}</div>;
}
